import React, { memo, useMemo, useCallback, useRef } from "react";
import { Container, useApp } from "@pixi/react";
import { Settings } from "../../settings";
import RoyalCollectReel from "./RoyalCollectReel";
import { useReelRefs } from "../../Contexts/ReelRefsContext";
import SpineTextureExtractor from "../../utils/SpineTextureExtractor";
import FontGenerator from "../../utils/FontGenerator";
import { useSelector } from "react-redux";
import { selectRoyalCollectInitialReels } from "../../State/selectors";

/**
 * RoyalCollectReelSet
 *
 * Top-level reel grid for the Royal Collect feature.
 * Always visible — the background animation handles viewport shifting
 * to reveal/hide this reelset. Constructs as soon as initialReels data arrives.
 */
const RoyalCollectReelSet = memo(
  ({ symbolWidth, symbolHeight, reelSpacing }) => {
    const app = useApp();
    const { royalCollectReelRefs } = useReelRefs();
    const initialReels = useSelector(selectRoyalCollectInitialReels);
    const reelRefs = useRef([]);

    // Callback ref to register reel with context
    const setReelRef = useCallback(
      (index) => (el) => {
        reelRefs.current[index] = el;
        if (royalCollectReelRefs.current) {
          royalCollectReelRefs.current[index] = el;
        }
      },
      [royalCollectReelRefs],
    );

    // Extract spine textures and generate fonts (same as main ReelSet)
    const { symbolsSpriteSheet, symbolsSpriteSheetBlurred } = useMemo(() => {
      if (app?.renderer) {
        SpineTextureExtractor.initialize(app.renderer);
        FontGenerator.initialize(app.renderer);
      }
      return {
        symbolsSpriteSheet: SpineTextureExtractor.getSymbols(),
        symbolsSpriteSheetBlurred: SpineTextureExtractor.getBlurredSymbols(),
      };
    }, [app?.renderer]);

    // Cache symbol data for texture lookups
    const reelSymbolsData = useMemo(() => {
      if (!symbolsSpriteSheet) return null;
      return (
        symbolsSpriteSheet.data?.frames || symbolsSpriteSheet.textures || null
      );
    }, [symbolsSpriteSheet]);

    // Container Y position — same formula as HoldAndWinReelSet
    const containerY = useMemo(
      () => -symbolHeight * (0.5 * Settings.SYMBOLS_VISIBLE - 0.5),
      [symbolHeight],
    );

    // Reel X positions — dynamic center offset from main ReelSet
    const reelPositions = useMemo(() => {
      if (!initialReels || initialReels.length === 0) return [];
      const centerOffset = (Settings.REEL_COUNT - 1) / 2;
      return initialReels.map((_, index) => ({
        x: reelSpacing * (index - centerOffset),
      }));
    }, [initialReels, reelSpacing]);

    // Guard: Don't render until initialReels and sprite textures are ready
    if (
      !initialReels ||
      !Array.isArray(initialReels) ||
      initialReels.length === 0 ||
      !symbolsSpriteSheet?.textures
    ) {
      return null;
    }

    return (
      <Container
        name="royal_collect_reelset"
        zIndex={3}
        x={0}
        y={containerY}
      >
        {initialReels.map((reel, index) => {
          const position = reelPositions[index];
          if (!position) return null;

          return (
            <RoyalCollectReel
              key={index}
              ref={setReelRef(index)}
              reel={reel}
              index={index}
              x={position.x}
              symbolWidth={symbolWidth}
              symbolHeight={symbolHeight}
              symbolsSpriteSheet={symbolsSpriteSheet}
              symbolsSpriteSheetBlurred={symbolsSpriteSheetBlurred}
              reelSymbolsData={reelSymbolsData}
            />
          );
        })}
      </Container>
    );
  },
);

RoyalCollectReelSet.displayName = "RoyalCollectReelSet";

export default RoyalCollectReelSet;
